import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import {
   ApplyAlternativeDeviceAddress,
   ResetAlternativeDeviceAddress,
   UserStatusAlternativeDeviceAddressChangedWithoutApplying,
} from './user-status-behaviour.actions';
import { SwitchAlternativeDevice } from '../../../../../samwin/samwin-acd-extension/samwin-acd-extension.actions';
import _ from 'lodash';
import { SamwinDeviceAddressChangedByServer } from '../../../../../samwin/samwin.actions';
import { TranslateService } from '@ngx-translate/core';
import { SamwinAcdExtensionStateSelectors } from 'src/app/store/samwin/samwin-acd-extension/samwin-acd-extension.selectors';

export interface UserStatusBehaviourStateModel {
   [brickInstanceId: string]: UserStatusBehaviourInstance;
}

export interface UserStatusBehaviourInstance {
   unappliedUserDeviceAddress: string;
}

export function defaultBehaviourInstance(): UserStatusBehaviourInstance {
   return {
      unappliedUserDeviceAddress: null,
   };
}

@State<UserStatusBehaviourStateModel>({
   name: 'userStatusBehaviour',
   defaults: {},
})
@Injectable()
export class UserStatusBehaviourState {
   constructor(private store: Store, private translateService: TranslateService) {}

   private static getSpecificBehaviourStateOrDefault(state: UserStatusBehaviourStateModel, brickInstanceId: string) {
      const value = state[brickInstanceId];
      if (value) {
         return value;
      } else {
         return defaultBehaviourInstance();
      }
   }

   @Selector([UserStatusBehaviourState])
   public static getUnappliedAlternativeDeviceAddressFn(state: UserStatusBehaviourStateModel) {
      return (brickInstanceId: string) => {
         const userStatusBehaviourInstance = UserStatusBehaviourState.getSpecificBehaviourStateOrDefault(state, brickInstanceId);
         return userStatusBehaviourInstance.unappliedUserDeviceAddress;
      };
   }

   @Action(UserStatusAlternativeDeviceAddressChangedWithoutApplying)
   @ImmutableContext()
   public userStatusAlternativeDeviceAddressChangedWithoutApplying(
      ctx: StateContext<UserStatusBehaviourStateModel>,
      action: UserStatusAlternativeDeviceAddressChangedWithoutApplying
   ) {
      ctx.setState((state: UserStatusBehaviourStateModel) => {
         const behaviour = UserStatusBehaviourState.getSpecificBehaviourStateOrDefault(state, action.brickInstanceId);
         behaviour.unappliedUserDeviceAddress = action.deviceAddress;
         state[action.brickInstanceId] = behaviour;
         return state;
      });
   }

   @Action(ApplyAlternativeDeviceAddress)
   public applyAlternativeDeviceAddress(ctx: StateContext<UserStatusBehaviourStateModel>, action: ApplyAlternativeDeviceAddress) {
      const newAlternativeDeviceAddress = this.store.selectSnapshot(UserStatusBehaviourState.getUnappliedAlternativeDeviceAddressFn)(action.brickInstanceId);
      if (_.isEmpty(newAlternativeDeviceAddress)) {
         const currentConfirmedDevice = this.store.selectSnapshot(SamwinAcdExtensionStateSelectors.getCurrentDeviceAddress);
         return this.store.dispatch(new SwitchAlternativeDevice(currentConfirmedDevice));
      } else if (newAlternativeDeviceAddress === this.translateService.instant('samwin.softphone.device_number')) {
         return this.store.dispatch(new ResetAlternativeDeviceAddress(action.brickInstanceId));
      }
      return this.store.dispatch(new SwitchAlternativeDevice(newAlternativeDeviceAddress));
   }

   @Action(SamwinDeviceAddressChangedByServer)
   @ImmutableContext()
   public samwinDeviceAddressChangedByServer(ctx: StateContext<UserStatusBehaviourStateModel>, action: SamwinDeviceAddressChangedByServer) {
      ctx.setState((state) => {
         _.forEach(state, (x) => (x.unappliedUserDeviceAddress = null));
         return state;
      });
   }

   @Action(ResetAlternativeDeviceAddress)
   @ImmutableContext()
   public resetAlternativeDeviceAddress(ctx: StateContext<UserStatusBehaviourStateModel>, action: ResetAlternativeDeviceAddress) {
      ctx.setState((state: UserStatusBehaviourStateModel) => {
         const behaviour = UserStatusBehaviourState.getSpecificBehaviourStateOrDefault(state, action.brickInstanceId);
         behaviour.unappliedUserDeviceAddress = null;
         state[action.brickInstanceId] = behaviour;
         return state;
      });
      return this.store.dispatch(new SwitchAlternativeDevice(''));
   }
}
